<template>
  <v-dialog v-model="isOpen" max-width="820" persistent>
    <div class="ai-visibility-dialog">
      <button type="button" class="ai-visibility-dialog__close" @click="close">
        <i class="material-icons">close</i>
      </button>

      <aside class="ai-visibility-dialog__side">
        <h2>Create AI Tracker</h2>
        <p>Track when AI recommends or cites your brand for customer questions.</p>
        <setup-step-nav :current-step="currentStepIndex"/>
        <p class="ai-visibility-dialog__note">
          AI answers can change. We rerun these questions regularly so you can spot trends
        </p>
      </aside>

      <div class="ai-visibility-dialog__divider"></div>

      <main class="ai-visibility-dialog__main">
        <div class="ai-visibility-dialog__body">
          <div v-if="isLoadingStep" class="ai-visibility-dialog__loading">
            <questions-loading-step v-if="loadingQuestions"/>
            <template v-else>
              <atom-spinner
                  :animation-duration="1000"
                  :size="70"
                  :color="'var(--v-yellow2Color-base)'"
              />
              <span>{{ loadingStepTitle }}</span>
            </template>
          </div>
          <template v-else>
            <brand-step
                v-if="currentStep.key === 'brand'"
                :brand-name="draft.brandName"
                :website-url="draft.websiteUrl"
                :validation-errors="validationErrors"
                @update:brandName="updateDraftField('brandName', $event)"
                @update:websiteUrl="updateDraftField('websiteUrl', $event)"
            />

            <market-step
                v-else-if="currentStep.key === 'market'"
                :market="draft.market"
                :location-code="draft.locationCode"
                :country-iso-code="draft.countryIsoCode"
                :language="draft.language"
                :language-code="draft.languageCode"
                @update-location="updateLocation"
                @update-language-option="updateLanguage"
            />

            <category-step
                v-else-if="currentStep.key === 'category'"
                :categories="categorySuggestions"
                :selected-categories="draft.categories"
                :validation-errors="validationErrors"
                @update:selectedCategories="updateDraftField('categories', $event)"
            />

            <prompts-step
                v-else-if="currentStep.key === 'prompts'"
                :groups="draft.questionGroups"
                :custom-questions="draft.customQuestions"
                :selected-question-ids="draft.selectedQuestionIds"
                :validation-errors="validationErrors"
                @toggle-question="toggleQuestion"
                @add-custom-question="addCustomQuestion"
                @add-custom-questions="addCustomQuestions"
                @update-custom-question="updateCustomQuestion"
                @delete-custom-question="deleteCustomQuestion"
            />

            <review-step
                v-else-if="creditPreview"
                :review-questions="reviewQuestions"
                :selected-question-ids="draft.selectedQuestionIds"
                :refresh-frequency="draft.refreshFrequency"
                :credit-preview="creditPreview"
                :validation-errors="validationErrors"
                @toggle-question="toggleQuestion"
                @update:refreshFrequency="updateDraftField('refreshFrequency', $event)"
            />
            <div v-else class="ai-visibility-dialog__action-error">
              Unable to load refresh cost.
            </div>
          </template>
        </div>

        <div class="ai-visibility-dialog__actions">
          <button
              type="button"
              class="ai-visibility-dialog__primary"
              :class="{ 'ai-visibility-dialog__primary--disabled': isPrimaryDisabled }"
              :disabled="isPrimaryDisabled"
              @click="handlePrimaryAction"
          >
            {{ primaryActionLabel }}
          </button>
          <div v-if="showQuestionSelectionError" class="ai-visibility-dialog__action-error">
            Select at least 1 question to start tracking.
          </div>
          <div v-if="actionErrorMessage" class="ai-visibility-dialog__action-error">
            {{ actionErrorMessage }}
          </div>
        </div>
      </main>
    </div>
  </v-dialog>
</template>

<script>
import debounce from 'lodash/debounce'
import {AtomSpinner} from 'epic-spinners'
import SetupStepNav from './SetupStepNav.vue'
import BrandStep from './steps/BrandStep.vue'
import CategoryStep from './steps/CategoryStep.vue'
import MarketStep from './steps/MarketStep.vue'
import PromptsStep from './steps/PromptsStep.vue'
import ReviewStep from './steps/ReviewStep.vue'
import QuestionsLoadingStep from './ui/QuestionsLoadingStep.vue'
import {AI_VISIBILITY_STEPS, EMPTY_SETUP_DRAFT} from '../constants'
import {getCategorySuggestions, getQuestionSuggestions, createAiVisibilityTracker} from '../api'
import {
  getFinalCreditTotal,
  getPreselectedQuestionIds,
  getQuestionsByIds,
  normalizeQuestionGroups,
} from '../utils'

// Message type Rocky (the assistant iframe) listens for. Sent on the same channel
// as `wizard-chips-ready` / `ai-writer-screen-ready` via PopAssistantButton.postToIframe.
const ROCKY_SCREEN_MESSAGE_TYPE = 'ai-visibility-screen-ready'

function createEmptyDraft() {
  return JSON.parse(JSON.stringify(EMPTY_SETUP_DRAFT))
}

const FIELD_STEP_MAP = {
  brandName: 'brand',
  websiteUrl: 'brand',
  categories: 'category',
  questions: 'prompts',
  refreshFrequency: 'review',
}

function getBackendErrorData(error) {
  return (error && error.response && error.response.data) ? error.response.data : {}
}

function getErrorMessage(value) {
  if (Array.isArray(value)) {
    return value[0] ? String(value[0]) : ''
  }
  return value ? String(value) : ''
}

export default {
  name: 'AiVisibilitySetupDialog',
  components: {
    AtomSpinner,
    SetupStepNav,
    BrandStep,
    CategoryStep,
    MarketStep,
    PromptsStep,
    QuestionsLoadingStep,
    ReviewStep,
  },
  props: {
    siteId: {
      type: [Number, String],
      default: null,
    },
    site: {
      type: Object,
      default: null,
    },
    latestTracker: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      isOpen: false,
      currentStepIndex: 0,
      draft: createEmptyDraft(),
      validationErrors: {},
      categorySuggestions: [],
      creditPreview: null,
      loadingCategories: false,
      loadingQuestions: false,
      submitting: false,
      actionErrorMessage: '',
      questionPollTimer: null,
    }
  },
  created() {
    // Debounced so rapid chip / checkbox toggles collapse into one Rocky update
    // (~1.2s, mirroring the ai-writer rockyReadback convention).
    this.emitRockyScreenDebounced = debounce(() => this.emitRockyScreen(), 1200)
  },
  beforeDestroy() {
    this.clearQuestionPollTimer()
    this.clearRockyScreen()
  },
  watch: {
    currentStepIndex () {
      this.emitRockyScreen()
    },
    categorySuggestions () {
      // Suggestions land after the step already switched to `category`.
      this.emitRockyScreen()
    },
    'draft.categories' () {
      this.emitRockyScreenDebounced()
    },
    'draft.selectedQuestionIds' () {
      this.emitRockyScreenDebounced()
    },
    'draft.refreshFrequency' () {
      this.emitRockyScreen()
    },
  },
  computed: {
    currentStep() {
      return AI_VISIBILITY_STEPS[this.currentStepIndex]
    },
    primaryActionLabel() {
      if (this.currentStep.key === 'review') {
        return this.submitting ? 'Creating...' : 'Run AI Tracker'
      }
      return 'Next step'
    },
    isPrimaryDisabled() {
      if (this.submitting || this.loadingCategories || this.loadingQuestions) {
        return true
      }
      if (this.currentStep.key === 'review' && !this.creditPreview) {
        return true
      }
      return ['prompts', 'review'].includes(this.currentStep.key) &&
          this.draft.selectedQuestionIds.length === 0
    },
    isLoadingStep() {
      return this.loadingCategories || this.loadingQuestions || this.submitting
    },
    loadingStepTitle() {
      if (this.submitting) {
        return 'Creating tracker...'
      }
      return this.loadingQuestions ? 'Generating questions...' : 'Finding categories...'
    },
    showQuestionSelectionError() {
      return this.currentStep.key === 'prompts' && this.draft.selectedQuestionIds.length === 0
    },
    reviewQuestions() {
      return getQuestionsByIds({
        groups: this.draft.questionGroups,
        customQuestions: this.draft.customQuestions,
        questionIds: this.draft.reviewQuestionIds,
      })
    },
    defaultBrandName() {
      let name = ''
      if (this.latestTracker) {
        name = this.latestTracker.title || this.latestTracker.name
      } else {
        name = (this.site && this.site.name) ? this.capitalizeFirstLetter(this.site.name) : ''
      }
      return this.cleanBrandName(name)
    },
    defaultWebsiteUrl() {
      if (this.latestTracker) {
        return this.latestTracker.domain
      }
      return (this.site && this.site.baseURL) ? this.site.baseURL : ''
    },
    defaultMarket() {
      return (this.latestTracker && this.latestTracker.market)
          ? this.latestTracker.market
          : EMPTY_SETUP_DRAFT.market
    },
    defaultLocationCode() {
      return (this.latestTracker && this.latestTracker.locationCode)
          ? this.latestTracker.locationCode
          : EMPTY_SETUP_DRAFT.locationCode
    },
    defaultCountryIsoCode() {
      return (this.latestTracker && this.latestTracker.countryIsoCode)
          ? this.latestTracker.countryIsoCode
          : EMPTY_SETUP_DRAFT.countryIsoCode
    },
    defaultLanguage() {
      return (this.latestTracker && this.latestTracker.language)
          ? this.latestTracker.language
          : EMPTY_SETUP_DRAFT.language
    },
    defaultLanguageCode() {
      return (this.latestTracker && this.latestTracker.languageCode)
          ? this.latestTracker.languageCode
          : EMPTY_SETUP_DRAFT.languageCode
    },
  },
  methods: {
    capitalizeFirstLetter(val) {
      return String(val).charAt(0).toUpperCase() + String(val).slice(1);
    },
    cleanBrandName(name) {
      return name.replace(/(\.[a-z]{2,})+$/i, '')
    },
    emitRockyScreen(stepOverride) {
      const step = typeof stepOverride === 'string' ? stepOverride : this.currentStep.key
      // Buffered send: PopAssistantButton keeps the latest screen and replays
      // it on iframe load, so a Rocky panel opened later still sees this step.
      this.$root.$refs.popAssistantButton?.postScreenToIframe(
        ROCKY_SCREEN_MESSAGE_TYPE,
        this.buildRockyScreenPayload(step),
      )
    },
    buildRockyScreenPayload(step) {
      // Number() guard: the prop comes from parseInt($route.params.siteId) and
      // can be NaN, which would serialize to the string "NaN" and silently fail
      // Rocky's site-scope check.
      const numericSiteId = Number(this.siteId)
      const payload = {
        step,
        siteId: Number.isFinite(numericSiteId) ? String(numericSiteId) : null,
        context: {
          brand: this.draft.brandName,
          website: this.draft.websiteUrl,
          market: this.draft.market,
          language: this.draft.language,
        },
      }
      if (step === 'category') {
        payload.suggestions = (this.categorySuggestions || []).map((category) => {
          const suggestion = { label: category.label }
          if (category.confidence != null) {
            suggestion.confidence = category.confidence
          }
          if (category.reason) {
            suggestion.reason = category.reason
          }
          return suggestion
        })
        payload.selectedCategories = (this.draft.categories || []).map((category) => ({
          label: category.label,
        }))
      }
      if (step === 'prompts') {
        payload.groups = this.buildRockyGroups()
      }
      if (step === 'review') {
        payload.review = {
          selectedQuestionCount: this.draft.selectedQuestionIds.length,
          refreshCostCredits: this.creditPreview
            ? getFinalCreditTotal(this.draft.selectedQuestionIds, this.creditPreview)
            : null,
          refreshFrequency: this.draft.refreshFrequency,
        }
      }
      return payload
    },
    buildRockyGroups() {
      // `selected` reflects the authoritative selection (draft.selectedQuestionIds),
      // not the stale per-question flag from generation.
      const selectedIds = this.draft.selectedQuestionIds
      const toRockyQuestion = (question) => ({
        id: question.id,
        text: question.text,
        selected: selectedIds.includes(question.id),
      })
      const toRockyGroup = (id, label, questions) => {
        const rockyQuestions = questions.map(toRockyQuestion)
        return {
          id,
          label,
          selectedCount: rockyQuestions.filter((question) => question.selected).length,
          totalCount: rockyQuestions.length,
          questions: rockyQuestions,
        }
      }

      const groups = (this.draft.questionGroups || []).map((group) => (
        toRockyGroup(group.id, group.label, group.questions || [])
      ))
      if (this.draft.customQuestions && this.draft.customQuestions.length) {
        groups.push(toRockyGroup('custom', 'Custom', this.draft.customQuestions))
      }
      return groups
    },
    showDialog() {
      this.clearQuestionPollTimer()
      this.resetDraft()
      this.isOpen = true
      // Emit even when currentStepIndex was already 0 (the watcher wouldn't fire).
      this.$nextTick(() => this.emitRockyScreen())
    },
    close() {
      this.clearQuestionPollTimer()
      this.clearRockyScreen()
      this.isOpen = false
    },
    clearRockyScreen() {
      // Cancel any pending debounced emit and drop the buffered screen so a
      // Rocky iframe loaded later doesn't replay stale wizard state.
      if (this.emitRockyScreenDebounced) {
        this.emitRockyScreenDebounced.cancel()
      }
      this.$root.$refs.popAssistantButton?.clearScreenFromIframe(ROCKY_SCREEN_MESSAGE_TYPE)
    },
    resetDraft() {
      this.currentStepIndex = 0
      this.validationErrors = {}
      this.actionErrorMessage = ''
      this.categorySuggestions = []
      this.creditPreview = null
      this.draft = createEmptyDraft()
      this.draft.brandName = this.defaultBrandName
      this.draft.websiteUrl = this.defaultWebsiteUrl
      this.draft.market = this.defaultMarket
      this.draft.locationCode = this.defaultLocationCode
      this.draft.countryIsoCode = this.defaultCountryIsoCode
      this.draft.language = this.defaultLanguage
      this.draft.languageCode = this.defaultLanguageCode
    },
    validateCurrentStep() {
      const errors = {}

      if (this.currentStep.key === 'brand') {
        if (!this.draft.brandName.trim()) {
          errors.brandName = 'Please, enter your brand name'
        }
        if (!this.draft.websiteUrl.trim()) {
          errors.websiteUrl = 'Please, enter your website URL'
        }
      }

      if (this.currentStep.key === 'category' && this.draft.categories.length === 0) {
        errors.categories = 'Please enter at least 1 category.'
      }

      if (['prompts', 'review'].includes(this.currentStep.key) && this.draft.selectedQuestionIds.length === 0) {
        errors.questions = 'Select at least one question to start tracking.'
      }

      this.validationErrors = errors
      this.actionErrorMessage = ''
      return Object.keys(errors).length === 0
    },
    async handlePrimaryAction() {
      if (!this.validateCurrentStep()) {
        return
      }

      const stepKey = this.currentStep.key
      this.actionErrorMessage = ''

      if (stepKey === 'brand') {
        this.currentStepIndex += 1
        return
      }

      if (stepKey === 'market') {
        this.currentStepIndex += 1
        await this.loadCategorySuggestions()
        return
      }

      if (stepKey === 'category') {
        const loaded = await this.loadQuestionSuggestions()
        if (loaded) {
          this.currentStepIndex += 1
        }
        return
      }

      if (stepKey === 'review') {
        await this.createTracker()
        return
      }

      if (stepKey === 'prompts') {
        this.draft.reviewQuestionIds = this.draft.selectedQuestionIds.slice()
      }

      this.currentStepIndex += 1
    },
    async loadCategorySuggestions() {
      this.loadingCategories = true
      this.categorySuggestions = []
      try {
        const response = await getCategorySuggestions({
          brandName: this.draft.brandName,
          websiteUrl: this.draft.websiteUrl,
          locationCode: this.draft.locationCode,
          languageName: this.draft.language,
        })
        this.categorySuggestions = response.data.categories || []
      } catch (error) {
        this.handleBackendError(error, 'Could not load category suggestions.')
      } finally {
        this.loadingCategories = false
      }
    },
    async loadQuestionSuggestions() {
      this.loadingQuestions = true
      this.draft.questionGroups = []
      this.draft.selectedQuestionIds = []
      this.draft.reviewQuestionIds = []
      this.creditPreview = null
      this.clearQuestionPollTimer()
      // Rocky shows a "generating" screen while the task runs; `prompts` is emitted
      // by the currentStepIndex watcher once the step advances with full groups.
      this.emitRockyScreen('generating')
      try {
        // Generation runs as a Celery task (see ai_visibility_api.py) to dodge the
        // Cloudflare 120s timeout; kick it off then poll for the result.
        const response = await getQuestionSuggestions(this.getQuestionSuggestionPayload())
        const taskId = response.data && response.data.taskId
        if (!taskId) {
          throw new Error('Missing task id for question suggestions.')
        }
        const result = await this.pollQuestionSuggestions(taskId)
        const groups = normalizeQuestionGroups(result.groups || [])
        this.draft.questionGroups = groups
        this.draft.selectedQuestionIds = getPreselectedQuestionIds(groups)
        this.creditPreview = result.creditPreview
        return true
      } catch (error) {
        const message = (error && error.response && error.response.status === 429)
            ? 'You are generating questions too often. Please wait a bit and try again.'
            : 'Could not load question suggestions.'
        this.handleBackendError(error, message)
        // Generation failed and we stay on `category`; let Rocky follow us back.
        this.emitRockyScreen()
        return false
      } finally {
        this.loadingQuestions = false
      }
    },
    pollQuestionSuggestions(taskId) {
      // Poll the generic Celery task-status endpoint until the task settles.
      // The authoritative timeout lives in the backend task (6-min Celery
      // time_limit -> FAILURE); this ~7-min cap is only a safety backstop for a
      // dead worker / expired result so the spinner never hangs forever.
      const POLL_INTERVAL_MS = 3000
      const MAX_ATTEMPTS = 140
      return new Promise((resolve, reject) => {
        let attempts = 0
        let requestInFlight = false
        const settle = (fn, value) => {
          this.clearQuestionPollTimer()
          fn(value)
        }
        const tick = () => {
          if (requestInFlight) {
            return
          }
          attempts += 1
          if (attempts > MAX_ATTEMPTS) {
            settle(reject, new Error('Timed out generating question suggestions.'))
            return
          }
          requestInFlight = true
          this.$store.dispatch('getTaskStatus', taskId)
              .then((response) => {
                const data = (response && response.data) || {}
                if (data.status === 'SUCCESS') {
                  settle(resolve, data)
                } else if (data.status === 'FAILURE') {
                  settle(reject, new Error('Question suggestion generation failed.'))
                }
                // PENDING / PROGRESS: keep polling.
              })
              .catch((error) => {
                settle(reject, error)
              })
              .finally(() => {
                requestInFlight = false
              })
        }
        this.questionPollTimer = setInterval(tick, POLL_INTERVAL_MS)
        tick()
      })
    },
    clearQuestionPollTimer() {
      if (this.questionPollTimer) {
        clearInterval(this.questionPollTimer)
        this.questionPollTimer = null
      }
    },
    updateDraftField(field, value) {
      this.draft[field] = value
      if (this.validationErrors[field]) {
        this.validationErrors = {...this.validationErrors, [field]: null}
      }
      this.actionErrorMessage = ''
    },
    toggleQuestion(question) {
      const isSelected = this.draft.selectedQuestionIds.includes(question.id)
      this.draft.selectedQuestionIds = isSelected
          ? this.draft.selectedQuestionIds.filter((id) => id !== question.id)
          : this.draft.selectedQuestionIds.concat(question.id)
      this.validationErrors = {...this.validationErrors, questions: null}
    },
    addCustomQuestion(question) {
      this.draft.customQuestions = this.draft.customQuestions.concat(question)
      this.draft.selectedQuestionIds = this.draft.selectedQuestionIds.concat(question.id)
      this.validationErrors = {...this.validationErrors, questions: null}
    },
    addCustomQuestions(questions) {
      if (!questions.length) {
        return
      }

      this.draft.customQuestions = this.draft.customQuestions.concat(questions)
      this.draft.selectedQuestionIds = this.draft.selectedQuestionIds.concat(questions.map((question) => question.id))
      this.validationErrors = {...this.validationErrors, questions: null}
    },
    updateCustomQuestion({id, text}) {
      this.draft.customQuestions = this.draft.customQuestions.map((question) => (
          question.id === id ? {...question, text} : question
      ))
      this.validationErrors = {...this.validationErrors, questions: null}
      this.actionErrorMessage = ''
    },
    deleteCustomQuestion(question) {
      this.draft.customQuestions = this.draft.customQuestions.filter((item) => item.id !== question.id)
      this.draft.selectedQuestionIds = this.draft.selectedQuestionIds.filter((id) => id !== question.id)
      this.draft.reviewQuestionIds = this.draft.reviewQuestionIds.filter((id) => id !== question.id)
      this.validationErrors = {...this.validationErrors, questions: null}
      this.actionErrorMessage = ''
    },
    updateLocation({market, locationCode, countryIsoCode}) {
      this.draft.market = market
      this.draft.locationCode = locationCode
      this.draft.countryIsoCode = countryIsoCode
      this.actionErrorMessage = ''
    },
    updateLanguage({language, languageCode}) {
      this.draft.language = language
      this.draft.languageCode = languageCode
      this.actionErrorMessage = ''
    },
    getSubmitPayload() {
      return {
        ...this.draft,
        siteId: this.siteId,
      }
    },
    getQuestionSuggestionPayload() {
      return {
        brandName: this.draft.brandName,
        websiteUrl: this.draft.websiteUrl,
        categories: this.draft.categories,
        market: this.draft.market,
        locationCode: this.draft.locationCode,
        languageCode: this.draft.languageCode,
        languageName: this.draft.language,
      }
    },
    reloadUser() {
      return this.$store.dispatch("loadUser").then(response => {
        this.$store.commit("setUser", response.data.user);
      });
    },
    async createTracker() {
      this.submitting = true
      try {
        const response = await createAiVisibilityTracker(this.getSubmitPayload())
        await this.reloadUser()
        this.$emit('created', response.data.tracker)
        this.close()
      } catch (error) {
        this.handleBackendError(error, 'Could not create AI tracker.')
      } finally {
        this.submitting = false
      }
    },
    handleBackendError(error, fallbackMessage) {
      const data = getBackendErrorData(error)
      // flask-smorest nests body validation errors under `errors.json`;
      // fall back to a flat `errors` object for non-validation error payloads.
      const backendErrors = (data.errors && data.errors.json) || data.errors || {}
      const validationErrors = {}
      const generalErrorMessages = []
      const firstFieldKey = Object.keys(backendErrors).find((key) => FIELD_STEP_MAP[key])

      Object.keys(backendErrors).forEach((key) => {
        const message = getErrorMessage(backendErrors[key])
        if (FIELD_STEP_MAP[key]) {
          validationErrors[key] = message
        } else if (message) {
          generalErrorMessages.push(message)
        }
      })

      this.validationErrors = validationErrors

      if (firstFieldKey) {
        const stepIndex = AI_VISIBILITY_STEPS.findIndex((step) => step.key === FIELD_STEP_MAP[firstFieldKey])
        if (stepIndex >= 0) {
          this.currentStepIndex = stepIndex
        }
      }

      const generalMessage =
          getErrorMessage(data.error) ||
          getErrorMessage(data.msg) ||
          generalErrorMessages.join(' ')

      this.actionErrorMessage = generalMessage || (firstFieldKey ? '' : fallbackMessage)
    },
  },
}
</script>

<style lang="scss" scoped>

.ai-visibility-dialog {
  position: relative;
  display: flex;
  width: 820px;
  min-height: 730px;
  gap: 14px;
  border-radius: 8px;
  background: var(--v-mainBgColor-base);
  padding: 40px 24px;
}

.ai-visibility-dialog__close {
  position: absolute;
  top: 14px;
  right: 14px;
  display: flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  color: var(--v-mainColor-base);
  cursor: pointer;
  padding: 0;

  i {
    font-size: 18px;
  }
}

.ai-visibility-dialog__side {
  width: 326px;
  flex: 0 0 326px;
  padding-left: 24px;

  h2 {
    color: var(--v-mainColor-base);
    font-size: 24px;
    font-weight: 800;
    line-height: 1;
    margin: 0 0 16px;
  }

  > p {
    color: var(--v-blackColor-base);
    font-size: 14px;
    line-height: 1.4;
    margin: 0;
    max-width: 280px;
  }
}

.ai-visibility-dialog__note {
  color: var(--v-mainColor3-base) !important;
  margin-top: 24px !important;
}

.ai-visibility-dialog__divider {
  width: 1px;
  align-self: stretch;
  background: var(--v-gray12Color-base);
}

.ai-visibility-dialog__main {
  display: flex;
  width: 398px;
  flex-direction: column;
  justify-content: space-between;
  padding: 0 20px;
}

.ai-visibility-dialog__body {
  min-height: 560px;
}

.ai-visibility-dialog__loading {
  display: flex;
  min-height: 300px;
  flex: 1;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 16px;
  color: var(--v-mainColor-base);
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
}

.ai-visibility-dialog__actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: stretch;
}

.ai-visibility-dialog__primary {
  height: 44px;
  border: 0;
  border-radius: 30px;
  cursor: pointer;
  font-size: 16px;
  line-height: 1.5;
  padding: 10px 20px;
  width: 100%;
  background: var(--v-profileAnchorColor-base);
  color: var(--v-cardBg-base);
}

.ai-visibility-dialog__primary--disabled {
  background: var(--v-darkGrayColor-base);
  cursor: not-allowed;
}

.ai-visibility-dialog__action-error {
  color: var(--v-red11Color-base);
  font-size: 12px;
  letter-spacing: 0.12px;
  text-align: center;
}

@media (max-width: 880px) {
  .ai-visibility-dialog {
    width: 100%;
    min-height: auto;
    flex-direction: column;
  }

  .ai-visibility-dialog__side,
  .ai-visibility-dialog__main {
    width: 100%;
    flex-basis: auto;
    padding-left: 0;
  }

  .ai-visibility-dialog__divider {
    width: 100%;
    height: 1px;
  }
}
</style>
